import { useTranslation } from "react-i18next";

import type { BadgeComponentProps } from "../types";

import { Badge } from "@/components/ui/v2/badge";

/**
 * BadgeComponent - renders a badge with text and variant.
 * Supports static text from props or dynamic interpolation from data.text.
 */
function BadgeComponent({
  text,
  variant,
  dataKey,
  data,
}: BadgeComponentProps) {
  const { t } = useTranslation();

  // Resolve value from dataKey → data[dataKey] object or scalar
  const resolved = dataKey && data ? data[dataKey] : undefined;
  const resolvedObj = typeof resolved === "object" && resolved !== null
    ? (resolved as Record<string, unknown>)
    : undefined;

  const displayText =
    resolvedObj?.label != null
      ? t(String(resolvedObj.label), { defaultValue: String(resolvedObj.label) })
      : resolved != null && !resolvedObj
        ? t(String(resolved), { defaultValue: String(resolved) })
        : text != null && String(text).trim() !== ""
          ? t(text)
          : data != null && "label" in data && data.label != null
            ? t(String(data.label), { defaultValue: String(data.label) })
            : data != null && "text" in data
              ? t(String(data.text ?? ""), { defaultValue: String(data.text ?? "") })
              : "";

  const rawVariant =
    (resolvedObj?.variant != null ? String(resolvedObj.variant) : undefined) ??
    variant ??
    (data != null && "variant" in data ? String(data.variant ?? "") : "") ??
    "Default";

  if (!displayText) return null;

  // Map backend variant (PascalCase) to UI variant (lowercase)
  const variantKey = rawVariant.toLowerCase() as
    | "default"
    | "success"
    | "warning"
    | "error";
  const key = variantKey === "default" ? "neutral" : variantKey;
  return (
    <Badge variant={key} size="small">
      {displayText}
    </Badge>
  );
}

export { BadgeComponent };
